import React from 'react';
import QueryView from '../Common/QueryView';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';
import EmailConfigurationForm from './EmailConfigurationForm';
import useGetEmailSettings, { EmailSettingsDto } from '../Requests/EmailSettings/useGetEmailSettings';
import useUpdateEmailSettings, { EmailConfigurationUpdate } from '../Requests/EmailSettings/useUpdateEmailSettings';

const EmailSettingsPage: React.FC = () => {
  const getEmailSettings = useGetEmailSettings();

  return <QueryView
    query={getEmailSettings}
    renderData={settings => <EmailSettingsContent settings={settings} />} />
};

const EmailSettingsContent: React.FC<{ settings: EmailSettingsDto }> = ({ settings }) => {
  const displayMessage = useDisplayMessage();

  const updateEmailSettings = useUpdateEmailSettings({
    onSuccess: () => displayMessage.success("Email settings have been updated."),
    onError: (e) => displayMessage.fail(e.message),
  });

  const handleSave = (formData: EmailConfigurationUpdate) => {
    updateEmailSettings.request(formData);
  };

  return <>
    <h3 className="ka-blue mb-3">Email Service</h3>
    <div className="row">
      <p className="col-8 ms-5">
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        Your business has a monthly limit of 6,000 emails, which resets on the 1st of each month. If you exceed 150% (9,000 emails), sending may pause
        until the limit resets or additional capacity is approved. Track usage in your dashboard. To request more emails use the link below or call support.
      </p>
      <div className="col-8 ms-6">
        <div className="row justify-content-center">
          <div className="col-10">
            <div className="row m-2 p-3 mb-4">
              <div className="col-12 mb-3">
                <h4 className="mb-0">Email Statistics</h4>
              </div>
              <div className="col-6">
                <div className="mb-3 pb-2 border-bottom">
                  <span className="text-secondary">Queued</span>
                  <span className="float-end fw-bold">{settings.queueCount}</span>
                </div>
              </div>
              <div className="col-6">
                <div className="mb-3 pb-2 ps-1 border-bottom">
                  <span className="text-secondary">Failed</span>
                  <span className="float-end fw-bold text-warning">{settings.failedCount}</span>
                </div>
              </div>
              <div className="col-6">
                <div className="mb-3 pb-2 border-bottom">
                  <span className="text-secondary">Sent</span>
                  <span className="float-end fw-bold">{settings.sentCount}</span>
                </div>
              </div>
              <div className="col-6">
                <div className="mb-3 pb-2 ps-1 pe-0 border-bottom">
                  <span className="text-secondary">Success Rate</span>
                  <span className="float-end fw-bold text-success">{settings.successRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>
            <EmailConfigurationForm
              settings={settings}
              onSave={handleSave}
              isLoading={updateEmailSettings.isLoading}
            />
            <div className="ps-6">
              <hr></hr>
              <p><small>Increase Email Limit - Contact Kahler Support</small></p>
              <a href="https://www.kahlerautomation.com/support-request"><small>https://www.kahlerautomation.com/support-request</small></a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
};

export { EmailSettingsPage };